import { useEffect, useRef, useState } from "react";
import {
  createOrder,
  validatePromo,
  getAddresses,
  saveAddress,
  ApiError,
  type PricedCart,
  type PublicOrder,
  type PromoPreview,
  type SavedAddress,
  type CartItem,
} from "./api.ts";
import { formatMoney } from "./format.ts";
import { MapPicker } from "./MapPicker.tsx";
import { haptic } from "./telegram.ts";

function newIdempotencyKey(): string {
  return typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

/** Preset delivery windows — a scannable choice beats free-text for staff + user. */
const SLOTS = ["ASAP", "Within 1 hour", "Evening 18–21", "Late 21–23"];

/**
 * Checkout: collects delivery details and places a prepaid order. A stable
 * idempotency key is generated once per checkout attempt so retries can't
 * create duplicate orders.
 */
export function Checkout({
  items,
  priced,
  defaultName,
  freeVape,
  preorderNote,
  onPlaced,
  onBack,
}: {
  items: CartItem[];
  priced: PricedCart;
  defaultName?: string;
  /** Redeemable free-vape reward: credits held + the discount it would apply. */
  freeVape?: { credits: number; discountCents: number } | null;
  /** Shown when the store is closed but preorders are enabled. */
  preorderNote?: string | null;
  onPlaced: (order: PublicOrder) => void;
  onBack: () => void;
}) {
  const [name, setName] = useState(defaultName ?? "");
  const [address, setAddress] = useState("");
  const [contact, setContact] = useState("");
  const [deliveryWindow, setDeliveryWindow] = useState("");
  const [notes, setNotes] = useState("");
  const [coords, setCoords] = useState<{ lat: number; lng: number } | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const idempotencyKey = useRef(newIdempotencyKey());

  // Saved addresses (pick to auto-fill; optionally save a new one).
  const [saved, setSaved] = useState<SavedAddress[]>([]);
  const [saveThis, setSaveThis] = useState(false);
  useEffect(() => {
    getAddresses()
      .then((rows) => {
        setSaved(rows);
        const def = rows.find((r) => r.isDefault) ?? rows[0];
        if (def) applyAddress(def);
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const applyAddress = (a: SavedAddress) => {
    setName(a.recipient);
    setAddress(a.address);
    setContact(a.contact);
    if (a.lat != null && a.lng != null) setCoords({ lat: a.lat, lng: a.lng });
  };

  // Promo code.
  const [promoInput, setPromoInput] = useState("");
  const [promo, setPromo] = useState<PromoPreview | null>(null);
  const [promoBusy, setPromoBusy] = useState(false);
  const applyPromo = async () => {
    if (!promoInput.trim()) return;
    setPromoBusy(true);
    try {
      const res = await validatePromo(promoInput.trim(), items);
      setPromo(res);
      haptic(res.valid ? "success" : "error");
    } catch {
      setPromo({ valid: false, reason: "error" });
    } finally {
      setPromoBusy(false);
    }
  };

  // Free-vape stamp reward redemption.
  const canRedeemFreeVape = !!freeVape && freeVape.credits > 0 && freeVape.discountCents > 0;
  const [redeem, setRedeem] = useState(false);

  // Client-side preview of the discounted total (server re-clamps on submit).
  const promoDiscount = promo?.valid ? promo.discountCents ?? 0 : 0;
  const payableAfterPromo = Math.max(0, priced.totalCents - promoDiscount);
  const freeVapeApplied =
    redeem && canRedeemFreeVape
      ? Math.min(freeVape!.discountCents, payableAfterPromo)
      : 0;
  const finalTotal = Math.max(0, priced.totalCents - promoDiscount - freeVapeApplied);

  // Human-readable list of what still blocks the order — shown so the user is
  // never staring at a disabled button wondering why.
  const missing: string[] = [];
  if (name.trim() === "") missing.push("recipient name");
  if (address.trim() === "") missing.push("delivery address");
  if (contact.trim() === "") missing.push("contact phone");

  const canSubmit = missing.length === 0 && priced.valid && !submitting;

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      const order = await createOrder(
        {
          items,
          delivery: {
            name: name.trim(),
            address: address.trim(),
            contact: contact.trim(),
            window: deliveryWindow.trim() || undefined,
            notes: notes.trim() || undefined,
            lat: coords?.lat,
            lng: coords?.lng,
          },
          promoCode: promo?.valid ? promo.code : undefined,
          redeemFreeVape: freeVapeApplied > 0 ? true : undefined,
        },
        idempotencyKey.current,
      );
      // Best-effort: persist the address for next time if requested.
      if (saveThis) {
        void saveAddress({
          recipient: name.trim(),
          address: address.trim(),
          contact: contact.trim(),
          lat: coords?.lat ?? null,
          lng: coords?.lng ?? null,
          isDefault: saved.length === 0,
        }).catch(() => {});
      }
      haptic("success");
      onPlaced(order);
    } catch (e) {
      haptic("error");
      const msg = e instanceof ApiError ? e.message : "could not place order";
      setError(
        msg === "store_paused"
          ? "We're temporarily not taking orders — please try again later."
          : msg === "store_closed"
            ? "We're closed right now — please order during opening hours."
            : msg,
      );
      setSubmitting(false);
    }
  };

  const itemCount = items.reduce((sum, i) => sum + i.quantity, 0);

  return (
    <>
      <details className="order-summary" open>
        <summary className="order-summary-head">
          <span className="order-summary-title">
            🛒 {itemCount} item{itemCount === 1 ? "" : "s"}
          </span>
          <span className="order-summary-total">
            {formatMoney(priced.totalCents, priced.currency)}
          </span>
        </summary>
        <div className="order-summary-lines">
          {priced.lines.map((line) => (
            <div
              className="summary-line"
              key={`${line.productId}::${line.variantId ?? ""}`}
            >
              <span className="summary-line-name">
                {line.quantity}× {line.name}
                {line.variantName ? ` (${line.variantName})` : ""}
              </span>
              <span className="summary-line-total">
                {formatMoney(line.lineTotalCents, priced.currency)}
              </span>
            </div>
          ))}
        </div>
      </details>

      {preorderNote && <p className="note">{preorderNote}</p>}

      {saved.length > 0 && (
        <section className="card">
          <span className="field-label">Saved addresses</span>
          <div className="chips">
            {saved.map((a) => (
              <button
                key={a.id}
                type="button"
                className="chip"
                onClick={() => applyAddress(a)}
              >
                {a.label || a.address.slice(0, 24)}
                {a.isDefault ? " ★" : ""}
              </button>
            ))}
          </div>
        </section>
      )}

      <section className="card form">
        <Field
          label="Recipient name"
          value={name}
          onChange={setName}
          autoComplete="name"
        />
        <div className="field">
          <span className="field-label">Pin your delivery location</span>
          <MapPicker
            onCoords={(lat, lng) => setCoords({ lat, lng })}
            onAddress={setAddress}
          />
        </div>
        <Field
          label="Delivery address"
          value={address}
          onChange={setAddress}
          autoComplete="street-address"
          multiline
        />
        <Field
          label="Contact phone"
          value={contact}
          onChange={setContact}
          type="tel"
          inputMode="tel"
          autoComplete="tel"
          placeholder="+31 6 …"
        />

        <div className="field">
          <span className="field-label">Preferred time</span>
          <div className="chips">
            {SLOTS.map((s) => (
              <button
                key={s}
                type="button"
                className={`chip ${deliveryWindow === s ? "chip-active" : ""}`}
                onClick={() =>
                  setDeliveryWindow((prev) => (prev === s ? "" : s))
                }
              >
                {s}
              </button>
            ))}
          </div>
        </div>

        <Field
          label="Notes (optional)"
          value={notes}
          onChange={setNotes}
          placeholder="Floor, doorbell, landmark…"
          multiline
        />

        <label className="ack compact">
          <input
            type="checkbox"
            checked={saveThis}
            onChange={(e) => setSaveThis(e.target.checked)}
          />
          <span>Save this address for next time</span>
        </label>
      </section>

      <section className="card">
        <span className="field-label">Promo code</span>
        <div className="promo-row">
          <input
            className="field-input"
            value={promoInput}
            placeholder="Enter code"
            autoCapitalize="characters"
            onChange={(e) => {
              setPromoInput(e.target.value);
              setPromo(null);
            }}
          />
          <button
            type="button"
            className="btn secondary promo-apply"
            onClick={applyPromo}
            disabled={promoBusy || !promoInput.trim()}
          >
            {promoBusy ? "…" : "Apply"}
          </button>
        </div>
        {promo && promo.valid && (
          <p className="note">
            🎉 Code applied — you save{" "}
            {formatMoney(promo.discountCents ?? 0, priced.currency)}
          </p>
        )}
        {promo && !promo.valid && (
          <p className="note error">{promoReason(promo.reason)}</p>
        )}

        {canRedeemFreeVape && (
          <label className="ack compact points-toggle">
            <input
              type="checkbox"
              checked={redeem}
              onChange={(e) => setRedeem(e.target.checked)}
            />
            <span>
              🎁 Use a free vape (−{formatMoney(
                Math.min(freeVape!.discountCents, payableAfterPromo),
                priced.currency,
              )}
              ){freeVape!.credits > 1 ? ` · ${freeVape!.credits} available` : ""}
            </span>
          </label>
        )}
      </section>

      <section className="card totals">
        {(promoDiscount > 0 || freeVapeApplied > 0) && (
          <>
            <div className="row">
              <span className="row-label">Subtotal + delivery</span>
              <span className="row-value">
                {formatMoney(priced.totalCents, priced.currency)}
              </span>
            </div>
            {promoDiscount > 0 && (
              <div className="row discount">
                <span className="row-label">Promo {promo?.code}</span>
                <span className="row-value">
                  −{formatMoney(promoDiscount, priced.currency)}
                </span>
              </div>
            )}
            {freeVapeApplied > 0 && (
              <div className="row discount">
                <span className="row-label">🎁 Free vape</span>
                <span className="row-value">
                  −{formatMoney(freeVapeApplied, priced.currency)}
                </span>
              </div>
            )}
          </>
        )}
        <div className="row row-strong">
          <span className="row-label">Total (pay by link)</span>
          <span className="row-value">
            {formatMoney(finalTotal, priced.currency)}
          </span>
        </div>
      </section>

      {error && <p className="note error">{error}</p>}

      {!canSubmit && missing.length > 0 && (
        <p className="checkout-missing">Still needed: {missing.join(", ")}.</p>
      )}

      <button className="btn" onClick={submit} disabled={!canSubmit}>
        {submitting
          ? "Placing order…"
          : `Place order · ${formatMoney(finalTotal, priced.currency)}`}
      </button>
      <button className="btn secondary" onClick={onBack} disabled={submitting}>
        Back to cart
      </button>
    </>
  );
}

function promoReason(reason?: string): string {
  switch (reason) {
    case "not_found":
      return "That code doesn’t exist.";
    case "inactive":
      return "This code is no longer active.";
    case "not_started":
      return "This code isn’t active yet.";
    case "expired":
      return "This code has expired.";
    case "min_subtotal":
      return "Your order is below this code’s minimum.";
    case "usage_limit":
      return "This code has been fully redeemed.";
    case "per_user_limit":
      return "You’ve already used this code.";
    case "new_customer_only":
      return "This code is for first-time customers.";
    default:
      return "Couldn’t apply that code.";
  }
}

function Field({
  label,
  value,
  onChange,
  multiline,
  type,
  inputMode,
  autoComplete,
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  multiline?: boolean;
  type?: string;
  inputMode?: "text" | "tel" | "search" | "email" | "numeric";
  autoComplete?: string;
  placeholder?: string;
}) {
  return (
    <label className="field">
      <span className="field-label">{label}</span>
      {multiline ? (
        <textarea
          className="field-input"
          value={value}
          rows={2}
          placeholder={placeholder}
          autoComplete={autoComplete}
          onChange={(e) => onChange(e.target.value)}
        />
      ) : (
        <input
          className="field-input"
          value={value}
          type={type ?? "text"}
          inputMode={inputMode}
          placeholder={placeholder}
          autoComplete={autoComplete}
          onChange={(e) => onChange(e.target.value)}
        />
      )}
    </label>
  );
}
